import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';

import {bindAll} from '#core/utils/utils.js';
import {assignToHierarchy} from '#core/utils/config-utils.js';
import {isNative} from '#core/utils/context-utils.js';

import {AbstractFeature, allFeatures} from '#core/application/abstract-feature.js';
import {Localization} from '#core/localization.js';

import nativeXtremePushService from './dbx-native-xtremepush-service.js';

import SCHEMA from './xtremepush.schema.yaml';

const reduxInstance = MojitoCore.Services.redux;
const authenticationActions = MojitoServices.Authentication.actions;
const {xtremePushService} = MojitoServices.SportsContent.ContentNotifications;

class XtremePushFeature extends AbstractFeature {
    constructor(parent) {
        super(parent);

        this.initialized = false;
        this.xtremePushUserId = '';

        bindAll(this, 'handleLoginSuccess', 'handleLogout');
    }

    get name() {
        return 'XtremePush';
    }

    get configSchema() {
        return SCHEMA;
    }

    get ready() {
        return this.initialized && this.config.enabled;
    }

    get isThirdParty() {
        return true;
    }

    get isTracker() {
        return true;
    }

    setupConfigs() {
        super.setupConfigs(...arguments);
    }

    beforeMojito() {
        reduxInstance.actionListener.startListening({
            actionCreator: authenticationActions.loginSuccess,
            effect: this.handleLoginSuccess,
        });

        reduxInstance.actionListener.startListening({
            actionCreator: authenticationActions.logoutSuccess,
            effect: this.handleLogout,
        });

        return super.beforeMojito();
    }

    afterMojitoConfigBuild(mojitoConfig) {
        if (this.config.contentNotificationsEnabled) {
            this.cookieConsentGiven();
            assignToHierarchy(mojitoConfig, 'services.contentNotificationsService', {
                service: isNative() ? nativeXtremePushService : xtremePushService,
            });
        }

        return super.afterMojitoConfigBuild(mojitoConfig);
    }

    dispose() {
        reduxInstance.actionListener.stopListening({
            actionCreator: authenticationActions.loginSuccess,
            effect: this.handleLoginSuccess,
        });

        reduxInstance.actionListener.stopListening({
            actionCreator: authenticationActions.logoutSuccess,
            effect: this.handleLogout,
        });

        super.dispose();
    }

    _setUserId() {
        if (isNative()) {
            // todo contact Native to discuss the preferred notification format
            //  e.g. DBX using Portal can send a NAVIGATE message like `native://xtremepushUserId=123456789`
        } else {
            window.xtremepush && window.xtremepush('set', 'user_id', this.xtremePushUserId);
        }
    }

    cookieConsentGiven() {
        if (this.initialized) {
            return;
        }

        this.initialized = true;

        // Do not initialize SDK for native context
        if (!isNative()) {
            const sdkUrl = this.config.sdkUrl.replace('{{appKey}}', this.config.appKey);

            (function (p, u, s, h, e, r, l, i, b) {
                p['XtremePushObject'] = s;
                p[s] = function () {
                    (p[s].q = p[s].q || []).push(arguments);
                };
                i = u.createElement('script');
                i.async = 1;
                i.src = h;
                b = u.getElementsByTagName('script')[0];
                b.parentNode.insertBefore(i, b);
            })(window, document, 'xtremepush', sdkUrl);
        }
        // TODO Transmitting language to XtremePush must be removed when Mojito implements MOJ-10372.
        const lang = Localization.getCurrentLanguage();
        window.xtremepush && window.xtremepush('set', 'app_language', lang);

        this._setUserId();
    }

    handleLoginSuccess(action) {
        const {userInfo = {}} = action.payload;
        // userName value (e.g. "1-928597318") is actually a PAM user ID, see details at DBX-9754
        const {userName = ''} = userInfo;

        // OPAP and XP agreed on the PAM user id format to be sent to the Xtremepush / Salesforce
        // integration and requested to remove the PAM user id prefix "1-" from the user id. (DBX-9775)
        this.xtremePushUserId = userName.startsWith('1-') ? userName.replace('1-', '') : userName;

        this._setUserId();
    }

    handleLogout() {
        this.xtremePushUserId = '';

        this._setUserId();
    }
}

export const XtremePush = new XtremePushFeature(allFeatures);
